import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import {
  Calculator,
  FileText,
  Gavel,
  Info,
  Megaphone,
  Plus,
  TrendingDown,
  TrendingUp,
  Wrench,
} from '@plataforma/ui/iconos';
import { Etapas } from './Etapas';
import { useApi, useConsulta } from '@plataforma/web/api';
import { BajaVehiculo } from '../../componentes/Baja';
import { CompraSubasta } from '../../componentes/Compra';
import { ImportacionVehiculo } from '../../componentes/Importacion';
import { ValuacionVehiculo } from '../../componentes/Valuacion';
import { BitacoraCostos } from '../../componentes/Costos';
import { DocumentosVehiculo } from '../../componentes/Documentos';
import { EstimacionesVehiculo } from '../../componentes/Estimaciones';
import { CorregirFabrica } from '../../componentes/Fabrica';
import {
  ETIQUETAS_ESTADO,
  ETIQUETAS_RUTA,
  ETIQUETAS_SITUACION,
  fechaCorta,
  mxn,
  nombreVehiculo,
} from '../../formato';
import type { Costo, Resumen, Vehiculo as Ficha } from '../tipos';
import type { AvisosAlta } from '../Vehiculos';
import { usePuede } from '../../permisos';
import { PiezasVehiculo } from '../../componentes/PiezasVehiculo';
import { LoteVehiculo } from '../../componentes/Lote';
import { DictamenVehiculo } from '../../componentes/Dictamen';
import { RutaVehiculo } from '../../componentes/Ruta';
import { CasoImportacionVehiculo } from '../../componentes/Emplacado';
import { HistorialReparacion } from '../../componentes/HistorialReparacion';
import type { Lote } from '../tipos';

type Pestana = 'resumen' | 'compra' | 'costos' | 'taller' | 'documentos';

/**
 * Ficha del vehículo (arquitectura 2.16, MVP4 V5): sus etapas a la vista, los indicadores de
 * costo y margen (solo con permiso de montos) y su información en pestañas. Cada pestaña aparece
 * solo si el rol puede ver algo en ella; la elegida queda en la dirección (`?pestana=`).
 */
export function Vehiculo() {
  const { id } = useParams();
  const api = useApi();
  const permitido = usePuede();
  // Patio y taller no ven montos (tabla de permisos): ni se piden al API.
  const verMontos = permitido('montos.ver');
  const ficha = useConsulta<Ficha>(`/inventory/vehiculos/${id}`);
  const costos = useConsulta<Costo[]>(verMontos ? `/finance/vehiculos/${id}/costos` : null);
  const resumen = useConsulta<Resumen>(verMontos ? `/finance/vehiculos/${id}/resumen` : null);
  const lote = useConsulta<Lote>(`/sourcing/lotes/${id}`);
  const rentabilidad = useConsulta<{ horas: string; gananciaPorHoraMxn: string | null }>(
    verMontos ? `/finance/vehiculos/${id}/rentabilidad` : null,
  );
  const [error, setError] = useState<string | null>(null);
  const [busqueda, setBusqueda] = useSearchParams();
  const ubicacion = useLocation();
  const navegar = useNavigate();
  // Los avisos del alta se muestran una vez: al recargar ya no aparecen.
  const [avisosAlta] = useState(() => (ubicacion.state as AvisosAlta | null)?.avisos ?? []);
  useEffect(() => {
    if (ubicacion.state) void navegar(ubicacion.pathname, { replace: true, state: null });
  }, [ubicacion, navegar]);
  const v = ficha.datos;

  const cambiarEstado = async (estado: string) => {
    setError(null);
    try {
      await api('POST', `/inventory/vehiculos/${id}/estado`, { estado });
      ficha.recargar();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const recargarCostos = () => {
    costos.recargar();
    resumen.recargar();
  };

  if (ficha.error) return <p className="error">{ficha.error}</p>;
  if (!v) return <p className="subtitulo">Cargando…</p>;
  const r = resumen.datos;
  const negativo = r?.margenEsperadoMxn?.startsWith('-');

  const pestanas: { clave: Pestana; texto: string; Icono: typeof Info; visible: boolean }[] = [
    { clave: 'resumen', texto: 'Resumen', Icono: Info, visible: true },
    {
      clave: 'compra',
      texto: 'Compra e importación',
      Icono: Gavel,
      visible: verMontos || permitido('importacion.ver'),
    },
    { clave: 'costos', texto: 'Costos y valuación', Icono: Calculator, visible: verMontos },
    {
      clave: 'taller',
      texto: 'Taller y piezas',
      Icono: Wrench,
      visible:
        permitido('ordenes.ver') || permitido('piezas.estado') || permitido('estimaciones.ver'),
    },
    { clave: 'documentos', texto: 'Documentos', Icono: FileText, visible: true },
  ];
  const visibles = pestanas.filter((p) => p.visible);
  const pedida = busqueda.get('pestana') as Pestana | null;
  const pestana = visibles.some((p) => p.clave === pedida) ? (pedida as Pestana) : 'resumen';

  return (
    <>
      <div className="encabezado">
        <div>
          <div className="subtitulo">
            <Link to="/vehiculos">Vehículos</Link> ›
          </div>
          <h1>{nombreVehiculo(v)}</h1>
          <div className="vin">
            {v.vin} ·{' '}
            {v.vinVerificado
              ? 'dígito verificador correcto'
              : 'dígito verificador no cuadra (revisa el VIN; los autos de fuera de Norteamérica pueden no tenerlo)'}
            {v.datosManuales ? ' · datos de fábrica corregidos a mano' : ''}
          </div>
        </div>
        <div className="relleno" />
        <span className="etiqueta">{ETIQUETAS_ESTADO[v.estado] ?? v.estado}</span>
      </div>

      <Etapas estado={v.estado} />

      <div className="acciones-vehiculo">
        {!v.baja &&
          permitido('vehiculos.estado') &&
          v.siguientes.map((s) => (
            <button key={s} className="boton primario" onClick={() => void cambiarEstado(s)}>
              Pasar a «{ETIQUETAS_ESTADO[s] ?? s}»
            </button>
          ))}
        {permitido('ordenes.trabajar') && (
          <Link className="boton" to={`/taller/nueva?vehiculo=${v.id}`}>
            <Wrench size={16} aria-hidden="true" /> Abrir orden de taller
          </Link>
        )}
        {permitido('publicaciones.administrar') && (
          <Link className="boton" to={`/publicaciones/nueva?vehiculo=${v.id}`}>
            <Megaphone size={16} aria-hidden="true" /> Publicar
          </Link>
        )}
        {permitido('proveedores.solicitar') && (
          <Link className="boton" to={`/proveedores/nueva?vehiculo=${v.id}`}>
            <Plus size={16} aria-hidden="true" /> Pedir cotización
          </Link>
        )}
      </div>

      {error && <p className="error">{error}</p>}
      {avisosAlta.map((a) => (
        <p key={a} className="error">
          {a}
        </p>
      ))}
      {!v.datosManuales &&
        v.avisosVin.map((a) => (
          <p key={a} className="aviso-caja">
            {a}
          </p>
        ))}

      {verMontos && (
        <div className="tarjetas">
          <div className="tarjeta">
            <div className="rotulo">Costo puesto en lote</div>
            <div className="cifra">{r ? mxn(r.costoPuestoEnLoteMxn) : '—'}</div>
            <div className="nota">Adquisición + traslado + importación</div>
          </div>
          <div className="tarjeta">
            <div className="rotulo">Costo total</div>
            <div className="cifra">{r ? mxn(r.costoTotalMxn) : '—'}</div>
            <div className="nota">
              {r && Number(r.estimadoMxn) > 0
                ? `${mxn(r.estimadoMxn)} todavía estimado`
                : 'Todo real'}
            </div>
          </div>
          <div className="tarjeta">
            <div className="rotulo">Valor esperado de venta</div>
            <div className="cifra">{r ? mxn(r.valorEsperadoVentaMxn) : '—'}</div>
            <div className="nota">Valuación vigente</div>
          </div>
          <div className={`tarjeta margen${negativo ? ' negativo' : ''}`}>
            <div className="rotulo">
              {negativo ? (
                <TrendingDown size={16} aria-hidden="true" />
              ) : (
                <TrendingUp size={16} aria-hidden="true" />
              )}{' '}
              Margen esperado{negativo ? ' (pérdida)' : ''}
            </div>
            <div className="cifra">{r ? mxn(r.margenEsperadoMxn) : '—'}</div>
            <div className="nota">Valor esperado − costo total</div>
          </div>
          <div className="tarjeta">
            <div className="rotulo">Ganancia por hora</div>
            <div className="cifra">
              {rentabilidad.datos?.gananciaPorHoraMxn
                ? mxn(rentabilidad.datos.gananciaPorHoraMxn)
                : '—'}
            </div>
            <div className="nota">
              {rentabilidad.datos && Number(rentabilidad.datos.horas) > 0
                ? `Margen ÷ ${rentabilidad.datos.horas} horas de taller`
                : 'Sin horas de taller registradas'}
            </div>
          </div>
        </div>
      )}

      <div className="pestanas" role="tablist" aria-label="Información del vehículo">
        {visibles.map(({ clave, texto, Icono }) => (
          <button
            key={clave}
            role="tab"
            id={`pestana-${clave}`}
            aria-selected={pestana === clave}
            aria-controls={`panel-${clave}`}
            className={pestana === clave ? 'activa' : undefined}
            onClick={() =>
              setBusqueda(
                (b) => {
                  const n = new URLSearchParams(b);
                  if (clave === 'resumen') n.delete('pestana');
                  else n.set('pestana', clave);
                  return n;
                },
                { replace: true },
              )
            }
          >
            <Icono size={16} aria-hidden="true" /> {texto}
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id={`panel-${pestana}`}
        aria-labelledby={`pestana-${pestana}`}
        className="panel-pestana"
      >
        {pestana === 'resumen' && (
          <>
            <section className="seccion">
              <header>
                <h2>Ficha</h2>
                <div className="relleno" />
                {permitido('vehiculos.registrar') && (
                  <CorregirFabrica v={v} alGuardar={ficha.recargar} />
                )}
              </header>
              <dl className="cuerpo resumen">
                {(
                  [
                    ['Título', v.titulo],
                    ['Motor', v.motor],
                    ['Planta', v.paisPlanta],
                    [
                      'Kilometraje',
                      v.kilometraje !== null
                        ? `${v.kilometraje.toLocaleString('es-MX')} ${v.unidadKilometraje}`
                        : null,
                    ],
                    ['Daños', v.danos.join(', ')],
                    ['Ruta', ETIQUETAS_RUTA[v.ruta] ?? v.ruta],
                    ['Situación legal', ETIQUETAS_SITUACION[v.situacionLegal] ?? v.situacionLegal],
                    ['Alta', fechaCorta(v.creadoEn)],
                  ] as const
                ).map(([dt, dd]) => (
                  <div key={dt}>
                    <dt>{dt}</dt>
                    <dd>{dd || '—'}</dd>
                  </div>
                ))}
              </dl>
            </section>
            <RutaVehiculo v={v} alGuardar={ficha.recargar} />
            <LoteVehiculo
              v={v}
              alGuardar={() => {
                lote.recargar();
                recargarCostos();
              }}
            />
            {permitido('vehiculos.baja') && <BajaVehiculo v={v} alCambiar={ficha.recargar} />}
          </>
        )}
        {pestana === 'compra' && (
          <>
            {verMontos && <DictamenVehiculo v={v} resumen={r ?? null} alGuardar={recargarCostos} />}
            {verMontos && (
              <CompraSubasta
                v={v}
                alGuardar={() => {
                  ficha.recargar();
                  recargarCostos();
                }}
              />
            )}
            {verMontos && <ImportacionVehiculo v={v} alRegistrar={recargarCostos} />}
            {permitido('importacion.ver') && (
              <CasoImportacionVehiculo
                v={v}
                lote={lote.datos ?? null}
                alRegistrar={() => {
                  ficha.recargar();
                  recargarCostos();
                }}
              />
            )}
          </>
        )}
        {pestana === 'costos' && (
          <>
            <ValuacionVehiculo v={v} alGuardar={recargarCostos} />
            <BitacoraCostos costos={costos} vehiculoId={v.id} alGuardar={recargarCostos} />
          </>
        )}
        {pestana === 'taller' && (
          <>
            {permitido('ordenes.ver') && <HistorialReparacion vehiculoId={v.id} />}
            {permitido('estimaciones.ver') && (
              <EstimacionesVehiculo vehiculoId={v.id} alAprobar={recargarCostos} />
            )}
            {permitido('piezas.estado') && <PiezasVehiculo vehiculoId={v.id} />}
          </>
        )}
        {pestana === 'documentos' && <DocumentosVehiculo vehiculoId={v.id} />}
      </div>
    </>
  );
}
